/**
 * Encapsulamento do MediaRecorder API pra capturar áudio do microfone.
 * Retorna um File pronto pra upload.
 */

interface AudioRecorderHandle {
  start: () => Promise<void>;
  stop: () => Promise<File>;
  cancel: () => void;
  isSupported: () => boolean;
}

export function createAudioRecorder(): AudioRecorderHandle {
  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: BlobPart[] = [];
  let stopPromise: { resolve: (f: File) => void; reject: (e: Error) => void } | null = null;

  const isSupported = (): boolean =>
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof MediaRecorder !== 'undefined';

  const cleanup = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    recorder = null;
    chunks = [];
    stopPromise = null;
  };

  const start = async (): Promise<void> => {
    if (!isSupported()) {
      throw new Error('MediaRecorder not supported');
    }
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Tipos suportados em ordem de preferência (webm é o mais universal nos browsers modernos)
    const mimeCandidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    const supportedMime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';

    recorder = supportedMime
      ? new MediaRecorder(stream, { mimeType: supportedMime })
      : new MediaRecorder(stream);
    chunks = [];

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
      const mimeType = supportedMime || 'audio/webm';
      const blob = new Blob(chunks, { type: mimeType });
      const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
      const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type });
      stopPromise?.resolve(file);
      cleanup();
    };

    recorder.start();
  };

  const stop = (): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (!recorder || recorder.state === 'inactive') {
        reject(new Error('Recorder not active'));
        return;
      }
      stopPromise = { resolve, reject };
      recorder.stop();
    });
  };

  const cancel = () => {
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      try {
        recorder.stop();
      } catch {
        // ignore
      }
    }
    cleanup();
  };

  return { start, stop, cancel, isSupported };
}
